import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { QButton, QTypography } from '@kw/quokka-ui';
import { Box, Portal } from '@mui/material';
import { KwConfirmationModal } from '../../kw-ui-components/KwConfirmationModal';
import {
  changeMdmIntegrationStatus,
  isPaused,
  isPausedBySupport,
  MdmIntegrationStatus,
  MdmIntegrationStatusAction,
  PAUSED_BY_SUPPORT_TEXT,
} from './mdmIntegrationStatus';

/** Pause / Resume button, used by Customer Support and on the organization's own Settings cards. */
interface MdmIntegrationStatusControlProps {
  /** Shown in the confirmation and snackbar messages. */
  label: string;
  status: MdmIntegrationStatus;
  /** Path the pause/resume action is appended to, e.g. `/organizations/customer-support/<orgId>/mdm-integrations/2`. */
  basePath: string;
  onChange: () => void;
  variant?: 'text' | 'outlined';
}

export const MdmIntegrationStatusControl = ({ label, status, basePath, onChange, variant = 'text' }: MdmIntegrationStatusControlProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const paused = isPaused(status);

  const submit = async (action: MdmIntegrationStatusAction) => {
    setSubmitting(true);
    const { error, message } = await changeMdmIntegrationStatus(basePath, action);
    setSubmitting(false);

    const verb = action === 'pause' ? 'paused' : 'resumed';
    if (error) {
      enqueueSnackbar(message ?? `Error updating ${label}`, { variant: 'error' });
    } else {
      enqueueSnackbar(`${label} ${verb}`, { variant: 'success' });
    }
    onChange();
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {paused ? (
        <QButton variant={variant} sx={{ whiteSpace: 'nowrap' }} disabled={submitting} onClick={() => submit('resume')}>
          Resume
        </QButton>
      ) : (
        <QButton variant={variant} sx={{ whiteSpace: 'nowrap' }} disabled={submitting} onClick={() => setConfirmOpen(true)}>
          Pause
        </QButton>
      )}
      {/* QModal renders a wrapper in place, which would resize the table cell; portal it out while open. */}
      {confirmOpen && (
        <Portal>
          <KwConfirmationModal
            title={`Pause ${label}`}
            isOpen={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            description={`pause ${label}`}
            extraDetails={
              <QTypography variant="bodyRegular">
                Scheduled and manual syncs stop until it is resumed. Credentials, devices and apps are kept.
              </QTypography>
            }
            onConfirm={() => submit('pause')}
          />
        </Portal>
      )}
    </Box>
  );
};

/** Pause notice on a Settings card. `status` is undefined until the integration is configured. */
export const MdmIntegrationCardStatus = ({ status }: { status?: MdmIntegrationStatus }) =>
  isPaused(status) ? (
    <QTypography sx={{ color: '#757575' }}>
      {isPausedBySupport(status)
        ? `${PAUSED_BY_SUPPORT_TEXT}. Contact Quokka Support to resume syncing.`
        : 'Paused. Syncing is stopped until it is resumed.'}
    </QTypography>
  ) : null;

/** The organization's own Pause / Resume, shown beside a Settings card's Edit button. Only Customer Support resumes its own pause. */
export const MdmIntegrationCardPauseButton = ({
  label,
  status,
  onChange,
}: {
  label: string;
  status?: MdmIntegrationStatus;
  onChange: () => void;
}) =>
  status && !isPausedBySupport(status) ? (
    <Box mr={1}>
      <MdmIntegrationStatusControl
        label={label}
        status={status}
        basePath={`/organizations/mdm-integrations/${status.mdmIntegrationId}`}
        onChange={onChange}
        variant="outlined"
      />
    </Box>
  ) : null;
